// src/pages/AdminLegal.js
//
// Viewer + editor for the Terms of Service and Privacy Policy, nested
// under Admin -> App Settings -> Legal. Platform-admin only.
//
// IMPORTANT: publishing a new version of EITHER document immediately
// revokes real access for every organizer who previously accepted --
// their stored tos_version (a single combined value covering both
// documents together) no longer matches, and _resolveOrganizer
// (middleware.js) now enforces that match. They'll each get a fresh
// re-acceptance email automatically (see routes/legal-admin.js) -- this
// is not a silent content edit, it's a real action with real
// consequences for every currently-active organizer, hence the
// confirmation step before publishing.

import React, { useState, useEffect } from 'react';
import { getLegalDocument, publishLegalDocument } from '../services/api';

const DOC_TYPES = [
    { id: 'terms',   label: 'Terms of Service' },
    { id: 'privacy', label: 'Privacy Policy' },
];

function AdminLegal({ isPlatformAdmin, showFeedback }) {
    const [docType, setDocType] = useState('terms');
    const [doc, setDoc] = useState(null);
    const [draft, setDraft] = useState('');
    const [loading, setLoading] = useState(true);
    const [publishing, setPublishing] = useState(false);
    const [confirming, setConfirming] = useState(false);

    useEffect(() => {
        if (!isPlatformAdmin) return;
        setLoading(true);
        setConfirming(false);
        getLegalDocument(docType)
            .then(res => {
                setDoc(res.data);
                setDraft(res.data.content);
            })
            .catch(err => showFeedback(err.response?.data?.error || 'Failed to load document', 'error'))
            .finally(() => setLoading(false));
    }, [docType, isPlatformAdmin, showFeedback]);

    if (!isPlatformAdmin) {
        return (
            <div className="max-w-lg mx-auto text-center py-12">
                <p className="text-gray-400 text-sm">
                    Only platform admins can view or edit the Terms of Service and Privacy Policy.
                </p>
            </div>
        );
    }

    const isDirty = doc && draft !== doc.content;

    const handlePublish = async () => {
        setPublishing(true);
        try {
            const res = await publishLegalDocument(docType, draft);
            setDoc(res.data.document);
            setDraft(res.data.document.content);
            setConfirming(false);
            showFeedback(
                `Published version ${res.data.document.version}. ` +
                `${res.data.organizersNotified} organizer${res.data.organizersNotified === 1 ? '' : 's'} ` +
                `sent a re-acceptance email.`
            );
        } catch (err) {
            showFeedback(err.response?.data?.error || 'Failed to publish', 'error');
        } finally {
            setPublishing(false);
        }
    };

    return (
        <div>
            <div className="flex gap-1 mb-6 border-b border-gray-800">
                {DOC_TYPES.map(dt => (
                    <button
                        key={dt.id}
                        onClick={() => setDocType(dt.id)}
                        className={'px-4 py-2 text-sm font-bold rounded-t-lg transition-colors ' +
                            (docType === dt.id
                                ? 'bg-gray-950 text-orange-400 border-t border-x border-gray-800'
                                : 'text-gray-500 hover:text-gray-300')}
                    >
                        {dt.label}
                    </button>
                ))}
            </div>

            {loading ? (
                <p className="text-gray-500 text-sm">Loading…</p>
            ) : (
                <div className="bg-gray-900 border border-gray-800 rounded-xl p-6">
                    <div className="flex items-center justify-between mb-4">
                        <div>
                            <h3 className="text-lg font-black text-white">
                                {DOC_TYPES.find(dt => dt.id === docType)?.label}
                            </h3>
                            <p className="text-gray-500 text-xs mt-1">
                                Current version: <span className="text-orange-400">{doc?.version}</span>
                                {doc?.published_at && (
                                    <> — published {new Date(doc.published_at).toLocaleDateString()}</>
                                )}
                            </p>
                        </div>
                    </div>

                    <textarea
                        value={draft}
                        onChange={e => setDraft(e.target.value)}
                        rows={20}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-white text-sm font-mono focus:outline-none focus:border-orange-500"
                    />

                    <div className="flex items-center justify-between mt-4">
                        <p className="text-gray-600 text-xs">
                            Publishing bumps the version and requires every currently-active
                            organizer to re-accept before they can use the platform again.
                        </p>
                        {!confirming ? (
                            <button
                                onClick={() => setConfirming(true)}
                                disabled={!isDirty || publishing}
                                className="bg-orange-500 hover:bg-orange-600 disabled:bg-orange-900 disabled:text-orange-700 text-white font-bold px-5 py-2 rounded-lg transition-colors text-sm whitespace-nowrap"
                            >
                                Publish new version
                            </button>
                        ) : (
                            <div className="flex items-center gap-2 whitespace-nowrap">
                                <span className="text-yellow-400 text-xs">Confirm publish?</span>
                                <button
                                    onClick={() => setConfirming(false)}
                                    className="text-gray-400 hover:text-gray-300 text-xs font-bold px-3 py-2"
                                >
                                    Cancel
                                </button>
                                <button
                                    onClick={handlePublish}
                                    disabled={publishing}
                                    className="bg-red-600 hover:bg-red-700 disabled:bg-red-900 text-white font-bold px-4 py-2 rounded-lg transition-colors text-sm"
                                >
                                    {publishing ? 'Publishing…' : 'Yes, publish & require re-acceptance'}
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminLegal;
